import { useTranslation } from 'react-i18next';
import { Dropdown } from './overlay';
import { LANGUAGES } from '../i18n/index';

/**
 * LanguageSelector — Language switcher using <Dropdown>.
 *
 * Features:
 *   - Flag + native language name in dropdown items
 *   - Beta badge for AI-translated languages with partial coverage
 *   - Verified badge prepared for native-speaker-reviewed languages
 *   - Primary languages (DE, EN) show no badge
 *
 * HTML lang attribute sync is handled in i18n/index.js via i18next events.
 *
 * Props:
 *   onLanguageChange?: (code: string) => void
 *     Optional callback when language is changed.
 */
export default function LanguageSelector({ onLanguageChange }) {
  const { i18n, t } = useTranslation();
  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];
  const visibleLanguages = LANGUAGES.filter(l => l.status !== 'hidden');

  const handleChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem('aif_language', code);
    if (onLanguageChange) onLanguageChange(code);
  };

  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[var(--aif-bg-tertiary)] transition-colors text-[var(--aif-text-primary)]"
          aria-label={t('language.switcher', 'Sprache wechseln')}
        >
          <span className="text-base leading-none" aria-hidden="true">
            {currentLang.flag}
          </span>
          <span className="text-sm font-medium uppercase">
            {currentLang.code}
          </span>
          <svg
            viewBox="0 0 12 12"
            fill="currentColor"
            className="w-3 h-3 text-[var(--aif-text-muted)]"
            aria-hidden="true"
          >
            <path d="M6 8.5L1.5 4h9L6 8.5z" />
          </svg>
        </button>
      </Dropdown.Trigger>
      <Dropdown.Content
        side="bottom"
        align="end"
        ariaLabel={t('language.switcher', 'Sprache wechseln')}
        className="min-w-[240px] max-h-[400px] overflow-y-auto"
      >
        <Dropdown.RadioGroup value={currentLang.code} onValueChange={handleChange}>
          {visibleLanguages.map((lang) => (
            <Dropdown.RadioItem
              key={lang.code}
              value={lang.code}
              className="flex items-center gap-3"
            >
              <span className="text-base leading-none" aria-hidden="true">
                {lang.flag}
              </span>
              <span className="flex-1">{lang.name}</span>
              {lang.status === 'beta' && (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-[var(--aif-color-warning)] opacity-70 flex-shrink-0"
                  aria-label={t('language.betaBadge', 'Beta')}
                  title={t('language.betaBadge', 'Beta')}
                />
              )}
              {lang.status === 'verified' && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-600 dark:text-green-400 font-medium"
                  aria-label={t('language.verifiedBadge', 'Verified')}
                >
                  ✓
                </span>
              )}
            </Dropdown.RadioItem>
          ))}
        </Dropdown.RadioGroup>
      </Dropdown.Content>
    </Dropdown.Root>
  );
}
